import { AfterViewInit, ChangeDetectorRef, Component, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { Router, Navigation, NavigationEnd } from '@angular/router';
import { MatSort } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';

import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { TableApiDataSource } from '../../../../app/api/data-sources/table.api-data-source';
import { AbstractApiService, Filter } from '../../../../app/api/services/abstract-api.service';
import { OrganizationData } from '../../../../app/modules/organizations/interfaces/OrganizationData';
import { OrganizationsService } from '../../../../app/modules/organizations/services/organizations.service';
import { UserPermission } from '../../../../app/modules/auth/enums/user-permission.enum';
import { TableSelectionModel, TypeWithId } from '../../../../app/api/data-sources/table-selection-model';
import { TableFooterComponent } from '../table-footer/table-footer.component';
import { AuthService } from '../../../../app/modules/auth/services/auth.service';
import { NamedEntityPropertyType } from '../../../interfaces/NamedEntityPropertyType';
import { IIndexable } from '../../../../app/modules/mdr-dashboards/interfaces/mdr-dashboards-widgets.interface';
import { CompaniesService } from '../../../../app/modules/companies/services/companies.service';
import { IdentityService } from '../../../../app/modules/auth/services/identity.service';
import { TimeframeService } from '../../../../app/modules/timeframe/services/timeframe.service';
import { CompanyDataModel } from '../../../../app/modules/companies/company-data-model.model';

interface CustomFilterColumn {
    name: string;
    value: string;
}

@Component({
    selector: 'app-entity-browser',
    templateUrl: './entity-browser.component.html',
    styleUrls: ['./entity-browser.component.scss'],
    providers: [EntityBrowserComponent],
    standalone: false,
})
export class EntityBrowserComponent<EntityData extends TypeWithId> implements AfterViewInit, OnDestroy {
    @HostBinding('class') componentClass = 'd-flex flex-grow flex-direction-column';

    @ViewChild(TableFooterComponent) paginator: TableFooterComponent;
    @ViewChild(MatSort) sort: MatSort;

    public entityDataSource: TableApiDataSource<EntityData>;
    public UserPermission = UserPermission;
    public displayedColumns: string[];
    public forceCompanyColumn = false;
    public ignoreCompanyFilter = false;
    public deleteCompanyFilter = true;
    public customFilterColumns: CustomFilterColumn[];
    public hideFilters = false;
    public selection: TableSelectionModel<EntityData>;
    public selectedOrganization: OrganizationData | null = null;
    public activeFilters: string[] = [];
    public gridFilters: string[] = [];
    public highlightedRow: EntityData | null = null;
    public disableTooltip = false;

    public loadedCompanySub: Subscription;
    protected filter: Filter = {};
    protected CompanyFieldRequestName = 'company';

    protected filterSubject = new BehaviorSubject<Filter>({});
    protected clearAllFiltersSubject = new Subject<boolean>();

    protected routerSubscription: Subscription;

    constructor(
        private entityService: AbstractApiService<EntityData>,
        protected organizationsService: OrganizationsService,
        protected companiesService: CompaniesService,
        protected identityService: IdentityService,
        public authService: AuthService,
        protected changeDetector: ChangeDetectorRef,
        protected timeframeService: TimeframeService,
        protected router: Router,
    ) {
        this.entityDataSource = new TableApiDataSource<EntityData>(this.entityService);
        this.selection = new TableSelectionModel<EntityData>(this.entityDataSource);
    }

    private static createGridFilters(allFilters: string[]) {
        let filters = allFilters;
        const index = filters.indexOf('materialized_path');
        if (index > -1) {
            filters.splice(index, 1);
        }

        return filters;
    }

    public ngAfterViewInit(): void {
        this.entityDataSource.MatPaginator = this.paginator;
        this.entityDataSource.MatSort = this.sort;
        this.entityDataSource.FilterObservable = this.combineTableFiltersWithCompanyFilter();
        this.changeDetector.detectChanges();
    }

    public ngOnDestroy(): void {
        this.entityDataSource.disconnect();
    }

    public refreshTable(): void {
        this.entityDataSource.refreshCurrentPage();
        this.selection.clear();
    }

    public getItemsCount(): number {
        return this.entityDataSource.Paginator ? this.entityDataSource.Paginator.length : 0;
    }

    public changeOrganization(newOrganization: OrganizationData | null, showCompanyColumnIfNeeded: boolean = true): void {
        const companyColumnKey = 'company';
        if (!this.identityService.companyLoaded) {
            this.loadedCompanySub = this.identityService.companyLoaded$.subscribe((isLoaded) => {
                if (isLoaded) {
                    this.filterTable(showCompanyColumnIfNeeded, newOrganization, companyColumnKey);
                }
            });
        } else {
            this.filterTable(showCompanyColumnIfNeeded, newOrganization, companyColumnKey);
        }
    }

    public filterTable(showCompanyColumnIfNeeded: boolean, newOrganization: any, companyColumnKey: string) {
        if (showCompanyColumnIfNeeded) {
            if (
                (newOrganization === null && this.identityService.isCompanySelectorVisible()) ||
                (this.forceCompanyColumn && CompanyDataModel.isCompanySelectorVisible(newOrganization?.details))
            ) {
                const hasActionsColumn = this.displayedColumns.includes('actions');

                /* add 'company' column before 'actions' if present as the last column */
                if (!this.displayedColumns.includes(companyColumnKey)) {
                    if (hasActionsColumn) {
                        this.displayedColumns.splice(
                            this.displayedColumns.findIndex((x) => x === 'actions'),
                            0,
                            companyColumnKey,
                        );
                    } else {
                        this.displayedColumns.push(companyColumnKey);
                    }
                }
            } else {
                this.displayedColumns = this.displayedColumns.filter((column) => column !== companyColumnKey);
            }
        }

        this.selection.clear();
        this.selectedOrganization = newOrganization;

        if (newOrganization && this.customFilterColumns && this.customFilterColumns.length) {
            this.customFilterColumns.forEach((filter: CustomFilterColumn) => {
                this.setFilterField(filter.name, (newOrganization as IIndexable)[filter.value], true);
            });
        } else {
            this.setFilterField(this.CompanyFieldRequestName, newOrganization, false);
        }
    }

    public isHighlightedRow(row: EntityData): boolean {
        return row === this.highlightedRow || this.selection.isSelectedItem(row);
    }

    public checkInputClientVisibility(event: MouseEvent, tooltip: MatTooltip | null = null, findClosest = '') {
        let target: Element | null = event.target as Element;

        if (findClosest) {
            target = target.closest(findClosest);
        }

        if (target) {
            this.disableTooltip = !(target.scrollWidth > target.clientWidth);
        }
    }

    public parseFilterItem(item: any, type: NamedEntityPropertyType): string {
        return item ? type.getName(item) : '';
    }

    public getClearAllFiltersObs() {
        return this.clearAllFiltersSubject.asObservable();
    }

    protected clearFilters() {
        this.activeFilters = [];
        this.gridFilters = [];
        this.organizationsService.selectAllOrganizations();
        this.clearAllFiltersSubject.next(true);
    }

    protected setFilterField(fieldName: string, newValue: any | any[], alterFilterObject: boolean = true): void {
        if (alterFilterObject) {
            if (newValue !== undefined && newValue !== null && newValue !== '') {
                this.filter[fieldName] = newValue;
            } else {
                delete this.filter[fieldName];
            }
            this.pushFilter();
        }

        if (newValue === undefined || newValue === null || newValue === '') {
            this.activeFilters = this.activeFilters.filter((field) => field !== fieldName);
            this.gridFilters = EntityBrowserComponent.createGridFilters(this.activeFilters.slice());
            return;
        }

        if (this.activeFilters.indexOf(fieldName) < 0) {
            this.activeFilters.push(fieldName);
            this.gridFilters = EntityBrowserComponent.createGridFilters(this.activeFilters.slice());
        }
    }

    private combineTableFiltersWithCompanyFilter(): Observable<Filter> {
        /* combine filter observable (table filters) & selected organization observable */
        return combineLatest([this.filterSubject.asObservable(), this.organizationsService.selectedOrganization$()]).pipe(
            map(([filter, organization]) => {
                /* add company filter if it is set */
                if (organization !== null) {
                    if (!this.ignoreCompanyFilter) {
                        filter[this.CompanyFieldRequestName] = organization.id.toString();
                    }

                    if (this.customFilterColumns && this.customFilterColumns.length) {
                        this.customFilterColumns.forEach((customFilter: CustomFilterColumn) => {
                            filter[customFilter.name] = (organization as IIndexable)[customFilter.value];
                        });
                    }
                } else {
                    if (this.deleteCompanyFilter) {
                        delete filter[this.CompanyFieldRequestName];
                    }

                    if (this.customFilterColumns && this.customFilterColumns.length) {
                        this.customFilterColumns.forEach((customFilter: CustomFilterColumn) => {
                            delete filter[customFilter.name];
                        });
                    }
                }

                return filter;
            }),
        );
    }

    private pushFilter(): void {
        this.filterSubject.next(this.filter);
    }

    /**
     * This function get data from router.
     * The main use-case is to filter companies from dashboard's drilldown
     */
    public getRouterEventsData() {
        this.routerSubscription = this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e: NavigationEnd) => {
                const navigation = this.router.getCurrentNavigation();

                if (!navigation?.extras.state) {
                    this.onRouterNavigationEndNoExtraState();
                    return;
                }

                const routerCid = navigation.extras.state ? navigation.extras.state.cid : '';
                const routerCompanyName = navigation.extras.state ? navigation.extras.state.company_name : '';
                const routerTimeframe = navigation.extras.state ? navigation.extras.state.timeframe : '';

                if (routerCid && routerCompanyName) {
                    this.organizationsService.selectOrganization({
                        id: routerCid,
                        name: routerCompanyName,
                    });
                }

                let filterDate: Date | null = null;

                if (routerTimeframe) {
                    let date = new Date();
                    date.setHours(0);
                    date.setMinutes(0);
                    date.setDate(date.getDate() - this.timeframeService.getTimeframeDays(routerTimeframe));
                    filterDate = date;
                }

                this.onRouterNavigationEnd(navigation, filterDate);
            });
    }

    public onRouterNavigationEndNoExtraState() {}

    public onRouterNavigationEnd(navigation: Navigation, filterDate: Date | null) {}
}
